import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { CURRENCY_STORAGE_KEY } from '../components/CurrencySelector'

interface TradeForCurrency {
  exit_date?: string | null
  pnl?: number | null
  currency?: string | null
}

/**
 * Get display currency: if all trades same currency use that, else use selected
 */
export function getDisplayCurrency(
  closedTrades: TradeForCurrency[],
  selectedCurrency: string
): string {
  const withCurrency = closedTrades.filter(
    (t) => t.exit_date && t.pnl != null && t.currency
  )
  if (withCurrency.length === 0) return selectedCurrency
  const unique = Array.from(new Set(withCurrency.map((t) => t.currency).filter(Boolean)))
  if (unique.length === 1) return unique[0] as string
  return selectedCurrency
}

/**
 * Convert amount from trade currency to display currency using latest exchange rates.
 * Used for open positions. Returns amount in display currency.
 */
export function convertToDisplayCurrency(
  amount: number,
  fromCurrency: string,
  displayCurrency: string,
  latestRates: Map<string, number>
): number {
  if (fromCurrency === displayCurrency) return amount
  const rate = latestRates.get(`${fromCurrency}:${displayCurrency}`)
  if (rate == null) return 0
  return amount * rate
}

/**
 * Convert P&L to display currency using exchange rates
 */
export function convertPnl(
  trade: { pnl?: number | null; exit_date?: string | null; currency?: string | null },
  displayCurrency: string,
  exchangeRates: Map<string, number>
): number {
  if (!trade.pnl || !trade.exit_date || !trade.currency) return trade.pnl ?? 0
  if (trade.currency === displayCurrency) return trade.pnl
  const date = trade.exit_date.split('T')[0]
  let rate = exchangeRates.get(`${date}:${displayCurrency}:${trade.currency}`)
  if (rate) return trade.pnl / rate
  const reverseRate = exchangeRates.get(`${date}:${trade.currency}:${displayCurrency}`)
  if (reverseRate) return trade.pnl * reverseRate
  return trade.pnl
}

export function useDashboardCurrency(userProfilePreferredCurrency?: string | null) {
  const [selectedCurrency, setSelectedCurrency] = useState<string>(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY)
    if (stored) return stored
    return userProfilePreferredCurrency || 'USD'
  })
  const [exchangeRates, setExchangeRates] = useState<Map<string, number>>(new Map())
  const [latestRates, setLatestRates] = useState<Map<string, number>>(new Map())

  useEffect(() => {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY)
    const curr = stored || userProfilePreferredCurrency || 'USD'
    setSelectedCurrency(curr)
  }, [userProfilePreferredCurrency])

  useEffect(() => {
    const handleChange = () => {
      const stored = localStorage.getItem(CURRENCY_STORAGE_KEY)
      if (stored) setSelectedCurrency(stored)
    }
    window.addEventListener('storage', handleChange)
    window.addEventListener('currencyChanged', handleChange)
    return () => {
      window.removeEventListener('storage', handleChange)
      window.removeEventListener('currencyChanged', handleChange)
    }
  }, [])

  /**
   * Fetch latest exchange rates for converting amounts to display currency.
   * Used for open positions (no exit_date). Key format: "fromCurrency:toCurrency" -> rate.
   * amount_in_to = amount_in_from * rate (when base=from, target=to in DB).
   */
  const fetchLatestExchangeRates = useCallback(
    async (displayCurrency: string, currencies: string[]) => {
      const unique = Array.from(new Set(currencies.filter((c) => c && c !== displayCurrency)))
      if (unique.length === 0) {
        setLatestRates(new Map())
        return
      }
      const ratesMap = new Map<string, number>()
      for (const fromCurrency of unique) {
        let { data: rateData } = await supabase
          .from('exchange_rates')
          .select('rate')
          .eq('base_currency', fromCurrency)
          .eq('target_currency', displayCurrency)
          .order('rate_date', { ascending: false })
          .limit(1)
          .maybeSingle()
        if (!rateData) {
          const { data: reverse } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', displayCurrency)
            .eq('target_currency', fromCurrency)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          if (reverse) {
            rateData = { rate: 1 / parseFloat(reverse.rate.toString()) }
          }
        }
        if (rateData) {
          ratesMap.set(`${fromCurrency}:${displayCurrency}`, parseFloat(rateData.rate.toString()))
        }
      }
      setLatestRates(ratesMap)
    },
    []
  )

  const fetchExchangeRates = useCallback(
    async (displayCurrency: string, closedTrades: TradeForCurrency[]) => {
      const needConversion = closedTrades.filter(
        (t) =>
          t.exit_date &&
          t.pnl != null &&
          t.currency &&
          t.currency !== displayCurrency
      )
      if (needConversion.length === 0) {
        setExchangeRates(new Map())
        return
      }
      const pairs = new Map<string, { date: string; currency: string }>()
      needConversion.forEach((t) => {
        if (!t.exit_date || !t.currency) return
        const date = t.exit_date.split('T')[0]
        const key = `${date}:${t.currency}`
        if (!pairs.has(key)) pairs.set(key, { date, currency: t.currency })
      })
      const ratesMap = new Map<string, number>()
      for (const { date, currency: tradeCurrency } of pairs.values()) {
        let { data: rateData } = await supabase
          .from('exchange_rates')
          .select('rate')
          .eq('base_currency', displayCurrency)
          .eq('target_currency', tradeCurrency)
          .eq('rate_date', date)
          .limit(1)
          .maybeSingle()
        if (!rateData) {
          const { data: nearest } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', displayCurrency)
            .eq('target_currency', tradeCurrency)
            .lte('rate_date', date)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          rateData = nearest ?? undefined
        }
        if (!rateData) {
          const { data: reverse } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', tradeCurrency)
            .eq('target_currency', displayCurrency)
            .lte('rate_date', date)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          if (reverse) {
            rateData = { rate: 1 / parseFloat(reverse.rate.toString()) }
          }
        }
        if (rateData) {
          ratesMap.set(
            `${date}:${displayCurrency}:${tradeCurrency}`,
            parseFloat(rateData.rate.toString())
          )
        }
      }
      setExchangeRates(ratesMap)
    },
    []
  )

  /**
   * Fetch exchange rates for converting position sizes (total_entry_amount).
   * Uses entry_date. Stores both key formats for convertPnl compatibility:
   * - date:display:trade = rate (1 display = rate trade) → divide
   * - date:trade:display = rate (1 trade = rate display) → multiply
   */
  const fetchExchangeRatesForEntryDates = useCallback(
    async (
      displayCurrency: string,
      trades: Array<{ entry_date?: string | null; currency?: string | null }>
    ) => {
      const needConversion = trades.filter(
        (t) =>
          t.entry_date &&
          t.currency &&
          t.currency !== displayCurrency
      )
      if (needConversion.length === 0) {
        return new Map<string, number>()
      }
      const pairs = new Map<string, { date: string; currency: string }>()
      needConversion.forEach((t) => {
        if (!t.entry_date || !t.currency) return
        const date = t.entry_date.split('T')[0]
        const key = `${date}:${t.currency}`
        if (!pairs.has(key)) pairs.set(key, { date, currency: t.currency })
      })
      const ratesMap = new Map<string, number>()
      for (const { date, currency: tradeCurrency } of pairs.values()) {
        let rate: number | null = null
        let reverseRate: number | null = null
        let { data: direct } = await supabase
          .from('exchange_rates')
          .select('rate')
          .eq('base_currency', displayCurrency)
          .eq('target_currency', tradeCurrency)
          .eq('rate_date', date)
          .limit(1)
          .maybeSingle()
        if (!direct) {
          const { data: nearest } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', displayCurrency)
            .eq('target_currency', tradeCurrency)
            .lte('rate_date', date)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          direct = nearest ?? undefined
        }
        if (direct) {
          rate = parseFloat(direct.rate.toString())
        } else {
          const { data: reverse } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', tradeCurrency)
            .eq('target_currency', displayCurrency)
            .lte('rate_date', date)
            .order('rate_date', { ascending: false })
            .limit(1)
            .maybeSingle()
          if (reverse) {
            reverseRate = parseFloat(reverse.rate.toString())
          }
        }
        if (rate != null) {
          ratesMap.set(`${date}:${displayCurrency}:${tradeCurrency}`, rate)
        }
        if (reverseRate != null) {
          ratesMap.set(`${date}:${tradeCurrency}:${displayCurrency}`, reverseRate)
        }
      }
      return ratesMap
    },
    []
  )

  return {
    selectedCurrency,
    exchangeRates,
    latestRates,
    fetchExchangeRates,
    fetchLatestExchangeRates,
    fetchExchangeRatesForEntryDates,
  }
}
